import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import { useThemeStore } from '../store/themeStore';
import { useSubscription } from '../features/subscription/SubscriptionContext';
import { notificationService } from '../services/notificationService';

export const UserAccountMenu = () => {
  const { user, signOut } = useAuth();
  const theme = useThemeStore((state) => state.theme);
  const toggleTheme = useThemeStore((state) => state.toggleTheme);
  const { subscriptionTier, hasStripeCustomer, manageSubscription } = useSubscription();
  const [isOpen, setIsOpen] = useState(false);
  const [isManaging, setIsManaging] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Get user initial for avatar
  const getUserInitial = () => {
    if (!user?.email) return '?';
    return user.email.charAt(0).toUpperCase();
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!user) {
    return null;
  }

  return (
    <div className="user-account-menu" ref={menuRef}>
      <button
        className="user-account-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Account menu"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="user-account-avatar">
          {getUserInitial()}
        </div>
      </button>
      {isOpen && (
        <div className="user-account-dropdown">
          <div className="user-account-dropdown-header">
            <div className="user-account-dropdown-avatar">
              {getUserInitial()}
            </div>
            <div className="user-account-dropdown-info">
              <div className="user-account-dropdown-email">{user.email}</div>
              <div className={`user-account-dropdown-tier tier-${subscriptionTier}`}>
                {subscriptionTier.charAt(0).toUpperCase() + subscriptionTier.slice(1)} Plan
              </div>
            </div>
          </div>
          <div className="user-account-dropdown-divider"></div>
          <div className="user-account-dropdown-menu">
            {!hasStripeCustomer ? (
              <Link
                to="/pricing"
                className="user-account-dropdown-item"
                onClick={() => setIsOpen(false)}
              >
                <span className="user-account-dropdown-item-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                  </svg>
                </span>
                <span className="user-account-dropdown-item-text">Upgrade to Pro</span>
              </Link>
            ) : (
              <button
                className="user-account-dropdown-item"
                onClick={async () => {
                  setIsManaging(true);
                  try {
                    await manageSubscription();
                  } catch (error) {
                    console.error('Failed to open subscription management:', error);
                    notificationService.showError('Failed to open subscription management. Please try again.');
                    setIsManaging(false);
                    setIsOpen(false);
                  }
                }}
                disabled={isManaging}
              >
                <span className="user-account-dropdown-item-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                    <rect x="2" y="5" width="20" height="14" rx="2" />
                    <line x1="2" y1="10" x2="22" y2="10" />
                  </svg>
                </span>
                <span className="user-account-dropdown-item-text">
                  {isManaging ? 'Opening...' : 'Manage Subscription'}
                </span>
              </button>
            )}
            <button
              className="user-account-dropdown-item"
              onClick={() => {
                toggleTheme();
                setIsOpen(false);
              }}
            >
              <span className="user-account-dropdown-item-icon">
                {theme === 'dark' ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                    <circle cx="12" cy="12" r="4" />
                    <path d="M12 2v2" />
                    <path d="M12 20v2" />
                    <path d="m4.93 4.93 1.41 1.41" />
                    <path d="m17.66 17.66 1.41 1.41" />
                    <path d="M2 12h2" />
                    <path d="M20 12h2" />
                    <path d="m6.34 17.66-1.41 1.41" />
                    <path d="m19.07 4.93-1.41 1.41" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
                  </svg>
                )}
              </span>
              <span className="user-account-dropdown-item-text">
                {theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              </span>
            </button>
            <button
              className="user-account-dropdown-item user-account-dropdown-item-danger"
              onClick={async () => {
                await signOut();
                setIsOpen(false);
              }}
            >
              <span className="user-account-dropdown-item-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </span>
              <span className="user-account-dropdown-item-text">Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

